---
title: Network Analysis
slug: network-analysis
type: work
status: published
featured: true
summary: Graph methods for offers, collaboration, and innovation ecosystems — centrality, co-occurrence, and PageRank-style ranking with explicit limits.
sitePublishedAt: "2026-09-18"
lastReviewedAt: "2026-10-02"
audiences:
  - business
  - product-developer
themes:
  - network-analysis
  - interaction-science
  - computational-social-science
sourceUrl: https://developer.ecosystem.ai/docs/configuration/algorithms/networkanalysis
originalAuthor: Jay van Zyl
sourceRelationship: first-party method note tied to public developer documentation
rightsStatus: first-party
seoDescription: "Jay van Zyl’s network-analysis method: directed offer graphs, co-occurrence and acceptance weights, centrality, and why sparse graphs must fall back."
canonicalPath: /work/network-analysis
---

The useful question is not “can we draw a graph?” It is: **what relationship, if we could see it, would change the next action?**

A node is a thing we can act on — an offer, a person, a capability, an organization. An edge is evidence of a relationship — co-presentation, acceptance, collaboration, shared context. Edge weight is not decoration; it is a claim about how often that evidence appears. Ranking then asks which nodes are structurally central, not merely popular in a last-click report.

## Three lenses

**Innovation ecosystems.** Mapping players, ideas, and connections so a portfolio is not a list of projects. The i4j Ecosystem Graph Project used this lens on jobs and the long-tail labor market.

**Organizational and collaboration networks.** Who actually carries work across formal lines. This is the academic interest in organizational network analysis, not an HR dashboard.

**Customer and offer interaction networks.** In [ecosystem.Ai Network Analysis](https://developer.ecosystem.ai/docs/configuration/algorithms/networkanalysis), offers are a directed graph. Acceptance and co-presentation form weighted edges. A Personalized PageRank-style rank surfaces bundles, complements, sequences, and related recommendations.

Workbench2 exposes `Network` as a dynamic-engagement learning approach on a project. That configuration is graph analysis of interaction structure. It is **not** Network Selector, Post Score Network, or runtime routing.

## What the synthetic graph on this page shows

The diagram is a **labeled synthetic** offer network for a retail-bank interaction, not a client graph. Current Account sits in the middle because it co-occurs with cards, savings, and a wallet. Personal Loan connects more weakly. Insurance is a complement, not a substitute. Node radius follows a simple centrality score; it is not a causal effect.

Graph ranking needs meaningful co-occurrence and acceptance data. On sparse or random graphs the algorithm must fall back; it is the wrong tool when offers are independent. A path is not a cause.

## Deterministic AI, then a reliable algorithm

The graph does not have to be assembled by hand each time. Deterministic AI can assist the process: propose the nodes, the edges, and the weights from evidence that is already collected, and leave that proposal open to inspection. The rank itself stays an algorithm with a stated fallback.

When that algorithm is reliable, it can be implemented on an AI platform. In ecosystem.Ai it is a learning approach on a project: the same graph question, scored while the person is still acting, with the response recorded for the next score.

## Lineage

The business-networking patent (WO2015004645A2) correlates profiles and suggests a connection past a threshold. The early ecosystem-analysis product asked market-positioning questions of network data. The current runtime asks a narrower, operational question: which offer is structurally central *in this interaction graph*, right now.
